import React, { useState } from "react";
import { db } from "../firebaseConfig";
import { collection, addDoc } from "firebase/firestore";
import * as Papa from "papaparse";
import { toast } from "react-toastify";
import "../styles/CSVUpload.css";

const CSVUpload = () => {
  const [csvData, setCsvData] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadMode, setUploadMode] = useState("csv");
  const [formData, setFormData] = useState({
    color: "",
    deviceId: "",
    imei: "",
    ownerUid: "",
    plateNumber: "",
    vehicleType: "",
  });

  // Handle CSV file upload
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      Papa.parse(file, {
        complete: (result) => {
          setCsvData(result.data.slice(1));
        },
        header: true,
      });
    }
  };

  // Handle CSV upload to Firestore
  const uploadToFirestore = async () => {
    if (csvData.length === 0) {
      toast.error("No data to upload!");
      return;
    }

    setUploading(true);
    try {
      const vehicleCollection = collection(db, "vehicles");

      for (const vehicle of csvData) {
        const plateNumber = vehicle["Plate Number"]?.trim();
        const imei = vehicle["IMEI"]?.trim();
        const deviceId = vehicle["Device ID"]?.trim();
        const vehicleType = vehicle["Vehicle Type"]?.trim();
        const color = vehicle["Color"]?.trim();
        const ownerUid = vehicle["Owner UID"]?.trim();

        if (!plateNumber || !imei || !deviceId || !vehicleType || !color || !ownerUid) {
          console.error("Skipping row due to missing data:", vehicle);
          continue;
        }

        await addDoc(vehicleCollection, {
          plateNumber,
          imei,
          deviceId,
          vehicleType,
          color,
          ownerUid,
        });
      }

      toast.success("Vehicles uploaded successfully!");
      setCsvData([]);
      setShowUploadForm(false);
    } catch (error) {
      console.error("Firestore Upload Error:", error);
      toast.error("Failed to upload vehicles!");
    } finally {
      setUploading(false);
    }
  };

  // Handle manual form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle manual form submission
  const handleManualSubmit = async (e) => {
    e.preventDefault();
    const { color, deviceId, imei, ownerUid, plateNumber, vehicleType } = formData;

    if (!color || !deviceId || !imei || !ownerUid || !plateNumber || !vehicleType) {
      toast.error("Please fill in all fields!");
      return;
    }

    setUploading(true);
    try {
      const vehicleCollection = collection(db, "vehicles");
      await addDoc(vehicleCollection, {
        color: color.trim(),
        deviceId: deviceId.trim(),
        imei: imei.trim(),
        ownerUid: ownerUid.trim(),
        plateNumber: plateNumber.trim(),
        vehicleType: vehicleType.trim(),
      });

      toast.success("Vehicle added successfully!");
      setFormData({
        color: "",
        deviceId: "",
        imei: "",
        ownerUid: "",
        plateNumber: "",
        vehicleType: "",
      });
      setShowUploadForm(false);
    } catch (error) {
      console.error("Firestore Manual Upload Error:", error);
      toast.error("Failed to add vehicle!");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="csv-upload-container">
      {/* Trigger Button */}
      <button className="btn dark" onClick={() => setShowUploadForm(!showUploadForm)}>
        ⬆️ Upload
      </button>

      {/* Modal Form */}
      {showUploadForm && (
        <div className="csv-upload-form">
          <h3>Add Vehicles</h3>
          {/* Mode Toggle */}
          <div className="upload-mode-toggle">
            <button
              className={uploadMode === "csv" ? "active" : ""}
              onClick={() => setUploadMode("csv")}
              disabled={uploading}
            >
              CSV Upload
            </button>
            <button
              className={uploadMode === "manual" ? "active" : ""}
              onClick={() => setUploadMode("manual")}
              disabled={uploading}
            >
              Manual Entry
            </button>
          </div>

          {/* CSV Upload */}
          {uploadMode === "csv" && (
            <div className="csv-upload-section">
              <p>Upload a CSV file with columns: Plate Number, IMEI, Device ID, Vehicle Type, Color, Owner UID</p>
              <input type="file" accept=".csv" onChange={handleFileUpload} disabled={uploading} />
              <button onClick={uploadToFirestore} disabled={uploading || csvData.length === 0}>
                {uploading ? "Uploading..." : "Upload to Firestore"}
              </button>
            </div>
          )}

          {/* Manual Entry Form */}
          {uploadMode === "manual" && (
            <form className="manual-entry-form" onSubmit={handleManualSubmit}>
              <table className="manual-entry-table">
                <tbody>
                  <tr>
                    <td><label htmlFor="plateNumber">Plate Number</label></td>
                    <td>
                      <input
                        type="text"
                        id="plateNumber"
                        name="plateNumber"
                        value={formData.plateNumber}
                        onChange={handleInputChange}
                        placeholder="Enter plate number"
                        disabled={uploading}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td><label htmlFor="imei">IMEI</label></td>
                    <td>
                      <input
                        type="text"
                        id="imei"
                        name="imei"
                        value={formData.imei}
                        onChange={handleInputChange}
                        placeholder="Enter IMEI"
                        disabled={uploading}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td><label htmlFor="deviceId">Device ID</label></td>
                    <td>
                      <input
                        type="text"
                        id="deviceId"
                        name="deviceId"
                        value={formData.deviceId}
                        onChange={handleInputChange}
                        placeholder="Enter device ID"
                        disabled={uploading}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td><label htmlFor="vehicleType">Vehicle Type</label></td>
                    <td>
                      <input
                        type="text"
                        id="vehicleType"
                        name="vehicleType"
                        value={formData.vehicleType}
                        onChange={handleInputChange}
                        placeholder="Enter vehicle type"
                        disabled={uploading}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td><label htmlFor="color">Color</label></td>
                    <td>
                      <input
                        type="text"
                        id="color"
                        name="color"
                        value={formData.color}
                        onChange={handleInputChange}
                        placeholder="Enter color"
                        disabled={uploading}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td><label htmlFor="ownerUid">Owner UID</label></td>
                    <td>
                      <input
                        type="text"
                        id="ownerUid"
                        name="ownerUid"
                        value={formData.ownerUid}
                        onChange={handleInputChange}
                        placeholder="Enter owner UID"
                        disabled={uploading}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
              <button type="submit" disabled={uploading}>
                {uploading ? "Adding..." : "Add Vehicle"}
              </button>
            </form>
          )}
        </div>
      )}
    </div>
  );
};

export default CSVUpload;